/**
 * La pagina pubblica dei biglietti (/registrazione-evento) parla con la
 * funzione evento-pubblico: qui le chiamate, le forme delle risposte e i
 * messaggi da mostrare. Senza accesso: il link personale e' l'unica chiave.
 */

import type { ErroriModulo, ModuloIscrizione } from '@corsi/modulo.ts';

const FUNZIONE = `${String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '')}/functions/v1/evento-pubblico`;

/** I link personali: 64 caratteri esadecimali. Un valore diverso non arriva nemmeno alla funzione. */
const TOKEN_VALIDO = /^[0-9a-f]{64}$/;
export const tokenValido = (valore: string | null): valore is string => Boolean(valore && TOKEN_VALIDO.test(valore));

export type StatoBiglietto = 'da_registrare' | 'registrato' | 'annullato';

export interface EventoPubblico {
  nome: string;
  email_riferimento: string | null;
}

export interface BigliettoPubblico {
  /** Solo per i biglietti ancora da registrare. */
  token: string | null;
  ordine: string;
  numero: number;
  prodotto: string;
  /** I biglietti abbinati (es. il Workshop col Congressista): stesso modulo, stessa persona. */
  insieme?: string[];
  stato: StatoBiglietto;
  partecipante: string | null;
}

export interface Acquirente {
  nome: string | null;
  cognome: string | null;
  email: string | null;
  telefono: string | null;
  azienda: string | null;
}

export interface PaginaAcquirente {
  evento: EventoPubblico;
  acquirente: Acquirente;
  biglietti: BigliettoPubblico[];
}

export interface PaginaBiglietto {
  evento: EventoPubblico;
  biglietto: BigliettoPubblico;
}

export type Esito<T> = { ok: true; dati: T } | { ok: false; codice: string; errori?: ErroriModulo };

async function chiama<T>(percorso: string, init?: RequestInit): Promise<Esito<T>> {
  try {
    const risposta = await fetch(`${FUNZIONE}${percorso}`, init);
    const corpo = await risposta.json().catch(() => ({}));
    if (risposta.ok) return { ok: true, dati: corpo as T };
    return { ok: false, codice: typeof corpo.errore === 'string' ? corpo.errore : 'errore_interno', errori: corpo.errori };
  } catch {
    return { ok: false, codice: 'rete' };
  }
}

export const leggiAcquirente = (token: string) => chiama<PaginaAcquirente>(`/biglietti?o=${encodeURIComponent(token)}`);

export const leggiBiglietto = (token: string) => chiama<PaginaBiglietto>(`/biglietto?b=${encodeURIComponent(token)}`);

export function registraBiglietto(token: string, modulo: ModuloIscrizione, trappola: string) {
  // biglietto_pdf: dopo la registrazione parte il biglietto in PDF per email.
  return chiama<{ ok: true; nome: string; evento: string; con_consenso: boolean; biglietto_pdf?: boolean }>('/biglietto', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Senza "?...": li' c'e' il link personale, che non deve finire nel lead.
    body: JSON.stringify({ b: token, modulo, sito: trappola, pagina: `${window.location.origin}${window.location.pathname}` }),
  });
}

/** "Congressista + Workshop Larry Wilson": il biglietto con quelli che vanno nello stesso modulo. */
export function nomeBiglietti(b: BigliettoPubblico): string {
  return [b.prodotto, ...(b.insieme ?? [])].join(' + ');
}

/** Quanti biglietti registra il modulo: per dire "il biglietto" o "i biglietti". */
export const quantiNelModulo = (b: BigliettoPubblico): number => 1 + (b.insieme?.length ?? 0);

/** Il link di un biglietto, da girare a chi lo usera'. */
export function linkBiglietto(token: string, origine: string = window.location.origin): string {
  return `${origine}/registrazione-evento?b=${token}`;
}

/** I dati di chi ha comprato, per "il biglietto e' per me". Solo quelli che il modulo ha. */
export function precompilaDaAcquirente(a: Acquirente): Partial<ModuloIscrizione> {
  const email = a.email ?? '';
  return {
    nome: a.nome ?? '',
    cognome: a.cognome ?? '',
    email,
    ripetiEmail: email,
    cellulare: a.telefono ?? '',
    azienda: a.azienda ?? '',
  };
}

/** Quanti biglietti mancano, per il titolo della pagina dell'acquirente. */
export function contaBiglietti(biglietti: BigliettoPubblico[]): { validi: number; registrati: number; daRegistrare: number } {
  const validi = biglietti.filter((b) => b.stato !== 'annullato');
  const registrati = validi.filter((b) => b.stato === 'registrato').length;
  return { validi: validi.length, registrati, daRegistrare: validi.length - registrati };
}

/** La frase per chi legge, dato il codice d'errore della funzione. */
export function messaggioErrore(codice: string, emailRiferimento?: string | null): string {
  const scrivi = emailRiferimento ? ` Se serve aiuto scrivi a ${emailRiferimento}.` : '';
  switch (codice) {
    case 'link_non_valido':
      return `Questo link non è valido. Controlla di averlo copiato per intero dall'email.${scrivi}`;
    case 'gia_registrato':
      return 'Questo biglietto è già stato registrato.';
    case 'biglietto_annullato':
      return `Questo biglietto è stato annullato.${scrivi}`;
    case 'persona_gia_registrata':
      return "Questa persona è già registrata all'evento con un altro biglietto. Ogni biglietto va a una persona diversa: inserisci i dati di chi userà questo.";
    case 'richiesta_non_valida':
      return 'Controlla i campi segnalati in rosso.';
    case 'evento_non_pronto':
      return `Le registrazioni per questo evento non sono ancora aperte.${scrivi}`;
    case 'troppe_richieste':
      return 'Troppi tentativi in poco tempo. Riprova fra qualche minuto.';
    case 'rete':
      return 'Connessione non riuscita. Controlla la rete e riprova.';
    default:
      return `Qualcosa non ha funzionato. Riprova fra qualche minuto.${scrivi}`;
  }
}
